import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { api, mediaUrl } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { trackEvent } from '@/lib/analytics';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator, BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import { toast } from 'sonner';
import { Copy, Eye, EyeOff, MessageSquare, Lock, Phone, Mail, User as UserIcon, MapPin, Tag, Package, BadgeCheck, Flag, ChevronLeft, Building2, Star, ChevronRight, Globe } from 'lucide-react';
import StatusPill from '@/components/marketplace/StatusPill';
import SEOHead from '@/components/marketplace/SEOHead';
import { SponsoredAdsRow } from '@/components/marketplace/SponsoredAd';
import FavoriteButton from '@/components/marketplace/FavoriteButton';
import ReviewsWidget from '@/components/marketplace/ReviewsWidget';
import ShareButton from '@/components/marketplace/ShareButton';
import { CompareDetailButton } from '@/components/marketplace/CompareCheckbox';
import ListingCard from '@/components/marketplace/ListingCard';
import { REPORT_REASONS } from '@/lib/constants';
import { listingPath } from '@/lib/listingUrl';
import { listingCategoryUrl } from '@/lib/taxonomyUrl';
import { resolveEffectiveOgImage, absoluteUrl } from '@/lib/ogImage';

// Neutral "No image" letterbox shown when a media URL fails to load, so a
// broken-image icon + alt text never bleeds over the card/badges.
const PLACEHOLDER_IMG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    "<svg xmlns='http://www.w3.org/2000/svg' width='320' height='200'><rect width='100%' height='100%' fill='#eef1f4'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='#9aa4b2' font-family='sans-serif' font-size='16'>No image</text></svg>"
  );

// WhatsApp brand mark — inline SVG so it stays crisp on any background and
// avoids an external icon dependency for a single glyph.
const WhatsAppIcon = ({ className = 'h-4 w-4' }) => (
  <svg viewBox='0 0 24 24' fill='currentColor' className={className} aria-hidden>
    <path d='M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.031 2C6.5 2 2 6.482 2 12.011c0 1.925.564 3.717 1.535 5.229L2 22l4.877-1.28a10.045 10.045 0 0 0 5.15 1.311h.004c5.532 0 10.032-4.483 10.032-10.011C22.063 6.482 17.564 2 12.031 2z' />
  </svg>
);

/** Compact star + rating chip */
const RatingChip = ({ avg, count, onClick }) => {
  if (avg == null || !count) return null;
  return (
    <button
      type='button'
      onClick={onClick}
      data-testid='seller-rating-chip'
      className='inline-flex items-center gap-1 text-[11px] font-medium rounded-full bg-yellow-500/10 text-yellow-700 dark:text-yellow-500 px-2 py-0.5 hover:bg-yellow-500/20 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500/50'
      title={`Average rating ${avg} from ${count} review${count === 1 ? '' : 's'} — click to view`}
    >
      <Star className='h-3 w-3 fill-yellow-500 text-yellow-500' /> {avg} <span className='text-muted-foreground font-normal'>({count})</span>
    </button>
  );
};

const ListingDetail = () => {
  const params = useParams();
  const routeKey = params.slug || params.id; // slug (new SEO URLs) or legacy uuid
  const { pathname } = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const id = listing?.id; // real UUID once loaded — used for all API + edit links
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState(null); // {mobile, email, contact_person}
  const [reviewRefreshKey, setReviewRefreshKey] = useState(0);
  const [selectedImage, setSelectedImage] = useState(0);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [inquiry, setInquiry] = useState({ name: '', company: '', email: '', mobile: '', message: '' });
  const [inquirySending, setInquirySending] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [report, setReport] = useState({ reason: '', details: '' });
  const [followBusy, setFollowBusy] = useState(false);
  const [isFollowing, setIsFollowing] = useState(null); // null=unknown, true/false=known
  const [similar, setSimilar] = useState([]);

  useEffect(() => {
    if (!user || !listing?.owner_id || listing.owner_id === user.id) {
      setIsFollowing(null);
      return;
    }
    // Fetch seller profile to know follow status
    api.get(`/sellers/${listing.owner_id}`)
      .then(r => setIsFollowing(!!r.data?.is_following))
      .catch(() => {});
  }, [user?.id, listing?.owner_id]);

  const toggleFollow = async () => {
    if (!user) return navigate('/login', { state: { from: `/listing/${id}` } });
    if (!listing?.owner_id) return;
    setFollowBusy(true);
    try {
      if (isFollowing) {
        await api.delete(`/follows/${listing.owner_id}`);
        setIsFollowing(false);
        toast.success('Unfollowed');
      } else {
        await api.post(`/follows/${listing.owner_id}`);
        setIsFollowing(true);
        toast.success('Following seller');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setFollowBusy(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/listings/${routeKey}`);
      setListing(data);
      // 301-style canonical redirect: send legacy /listing/<uuid> links and
      // stale category paths to the current SEO URL. Client-side replace +
      // the canonical <link> tag consolidate ranking on the clean URL.
      const canonical = listingPath(data);
      if (canonical && canonical !== pathname) {
        navigate(canonical, { replace: true });
      }
      // Analytics: listing_view is our top-of-funnel demand signal.
      trackEvent('listing_view', {
        listing_id: data.id,
        listing_type: data.listing_type,
        category: data.category_name,
        subcategory: data.subcategory_name,
        currency: 'INR',
        value: data.price || 0,
        content_ids: [data.id],
        content_type: 'product',
      });
    } catch (e) {
      toast.error('Listing not available');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [routeKey]);

  // Fetch "similar listings" whenever the current listing loads. Server
  // does the ranking cascade (same category > listing_type > city >
  // state); we just render whatever comes back. Silent-fail on error —
  // similar listings is a nice-to-have, not critical to the detail page.
  useEffect(() => {
    let cancelled = false;
    if (!id) return;
    api.get(`/listings/${id}/similar`, { params: { limit: 6 } })
      .then(r => { if (!cancelled) setSimilar(r.data.items || []); })
      .catch(() => { if (!cancelled) setSimilar([]); });
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (user && listing) {
      setInquiry(i => ({ ...i, name: user.name, email: user.email, mobile: user.mobile || '', company: user.company_name || '' }));
    }
  }, [user, listing]);

  const handleReveal = async () => {
    if (!user) return navigate('/login', { state: { from: `/listing/${id}` } });
    try {
      const { data } = await api.post(`/inquiries/reveal/listing/${id}`);
      setRevealed(data);
      // Revealing contact satisfies the review-eligibility rule (contact the
      // seller first), so re-check it immediately — no page refresh needed.
      setReviewRefreshKey(k => k + 1);
      toast.success('Contact revealed. Reveal has been logged.');
      // Analytics: contact_reveal is the highest-intent action — primary
      // conversion for the marketplace.
      trackEvent('contact_reveal', {
        listing_id: id,
        listing_type: listing?.listing_type,
        category: listing?.category_name,
      });
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reveal contact');
      return null;
    }
  };

  /**
   * WhatsApp CTA — paid-seller-only premium contact channel.
   *
   * Flow:
   *   1. Anonymous → send to /login (with return path).
   *   2. Logged in but contact not yet revealed → run reveal first so the
   *      reveal is properly logged (matches phone/email behaviour) and we
   *      have the seller's number to build the wa.me link.
   *   3. Open wa.me deeplink in a new tab with a pre-filled message that
   *      references the listing title so the seller has context.
   *
   * Note: the button is only rendered when `listing.owner_is_paid` is
   * true, so free-tier sellers never see it — no upgrade prompt to the
   * viewer (per product decision, we monetise seller-side).
   */
  const handleWhatsApp = async () => {
    if (!user) return navigate('/login', { state: { from: `/listing/${id}` } });
    let mobile = revealed?.mobile;
    if (!mobile) {
      const data = await handleReveal();
      mobile = data?.mobile;
    }
    if (!mobile) {
      toast.error('WhatsApp number not available for this listing.');
      return;
    }
    // Normalise: strip spaces / dashes / parens and ensure country code.
    let digits = String(mobile).replace(/[^\d+]/g, '');
    if (digits.startsWith('+')) digits = digits.slice(1);
    // Assume India (91) if a 10-digit number was stored without a prefix.
    if (digits.length === 10) digits = `91${digits}`;
    const title = listing?.title || 'your listing';
    const url = `${window.location.origin}${listing ? listingPath(listing) : `/listing/${id}`}`;
    const msg = `Hi, I'm interested in "${title}" on IndusFact. Could you share more details?\n${url}`;
    const waUrl = `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
    // Analytics: track WhatsApp click as a Contact event.
    trackEvent('whatsapp_click', {
      listing_id: id,
      listing_type: listing?.listing_type,
      category: listing?.category_name,
    });
    window.open(waUrl, '_blank', 'noopener,noreferrer');
  };

  const submitInquiry = async () => {
    if (!user) return navigate('/login', { state: { from: `/listing/${id}` } });
    if (!inquiry.message || inquiry.message.length < 5) return toast.error('Please write a proper message');
    setInquirySending(true);
    try {
      await api.post(`/inquiries/listing/${id}`, inquiry);
      toast.success('Inquiry sent to seller');
      setInquiryOpen(false);
      // Sending an inquiry also satisfies the review-eligibility rule.
      setReviewRefreshKey(k => k + 1);
      // Analytics: inquiry_sent \u2014 the primary lead event for the platform.
      trackEvent('inquiry_sent', {
        listing_id: id,
        listing_type: listing?.listing_type,
        category: listing?.category_name,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send inquiry');
    } finally { setInquirySending(false); }
  };

  const submitReport = async () => {
    if (!user) return navigate('/login', { state: { from: `/listing/${id}` } });
    if (!report.reason) return toast.error('Please choose a reason');
    try {
      await api.post('/reports', { target_type: 'listing', target_id: id, reason: report.reason, details: report.details });
      toast.success('Report submitted. Our moderators will review.');
      setReportOpen(false);
      setReport({ reason: '', details: '' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit report');
    }
  };

  if (loading) return <div className='indus-container py-16 text-center text-muted-foreground'>Loading listing…</div>;
  if (!listing) return (
    <div className='indus-container py-16 text-center' data-testid='listing-not-found'>
      {/* Soft-404 mitigation: SPAs can't return a real 404 status, so we
          emit robots:noindex here. Combined with sitemap exclusion + IndexNow
          delete pings, deleted / expired / non-live listings get deindexed. */}
      <SEOHead title='Listing not found | IndusFact' robots='noindex,follow' />
      Listing not found. <Link to='/' className='text-primary hover:underline'>Go home</Link>
    </div>
  );

  const images = (listing.media || []).filter(m => m.kind === 'image');
  const videos = (listing.media || []).filter(m => m.kind === 'video');
  const pdfs = (listing.media || []).filter(m => m.kind === 'pdf');
  const currentImage = images[selectedImage];
  const onRequest = listing.is_price_on_request || (
    !listing.asking_price && !listing.price_amount && !listing.budget_amount
  );
  const displayPrice = onRequest ? null : (listing.asking_price ?? listing.price_amount ?? listing.budget_amount);
  const priceType = listing.pricing_display || listing.price_type || listing.pricing_type || listing.budget_type;

  // Spec rows are strictly scoped to the listing's own type — a field only
  // renders for the listing_type it belongs to. This prevents attributes from
  // one type (e.g. Rent's Operator/Transport Included) from bleeding into
  // another (e.g. Sell) even when a stale/default value is present on the doc.
  const lt = listing.listing_type;
  const yn = (v) => (v === null || v === undefined ? null : (v ? 'Yes' : 'No'));
  const sellRows = lt === 'sell' ? [
    ['Condition', listing.condition],
    ['Year', listing.year_of_manufacture],
    ['Running Hours', listing.running_hours],
    ['Quantity', listing.quantity],
    ['Unit', listing.unit],
    ['Pricing Type', listing.price_type],
  ] : [];
  const wantedRows = lt === 'wanted' ? [
    ['Preferred Condition', listing.preferred_condition],
    ['Quantity Needed', listing.quantity_needed],
    ['Urgency', listing.urgency],
    ['Budget Type', listing.budget_type],
  ] : [];
  const rentRows = lt === 'rent' ? [
    ['Condition', listing.condition],
    ['Quantity Available', listing.quantity_available],
    ['Unit', listing.unit],
    ['Pricing Type', listing.pricing_type],
    ['Availability', listing.availability_status],
    ['Operator Included', yn(listing.operator_included)],
    ['Transport Included', yn(listing.transport_included)],
    ['Available From', listing.available_from],
    ['Min Rental Period', listing.minimum_rental_period],
    ['Max Rental Period', listing.maximum_rental_period],
  ] : [];
  const specRows = [
    ['Category', listing.category_name],
    ['Subcategory', listing.subcategory_name],
    ['Brand', listing.brand],
    ['Model', listing.model],
    ['Country of origin', listing.country_of_origin],
    ...sellRows,
    ...wantedRows,
    ...rentRows,
    ['Locality', listing.locality],
    ['State', listing.state],
    ['City', listing.city],
  ].filter(([, v]) => v !== null && v !== undefined && v !== '');

  const typePath = listing.listing_type === 'service' ? '/services' : `/${listing.listing_type}`;
  const typeLabel = listing.listing_type === 'service' ? 'SERVICES' : listing.listing_type.toUpperCase();
  const typeBadge = { sell: 'indus-badge-sell', wanted: 'indus-badge-wanted', service: 'indus-badge-service' }[listing.listing_type] || 'indus-badge-rent';
  const listingCountry = (listing.country || '').trim();
  const isIntlListing = !!listingCountry && listingCountry.toLowerCase() !== 'india';

  return (
    <div className='indus-container py-6' data-testid='listing-detail-page'>
      <SEOHead
        title={`${listing.seo?.meta_title || listing.title} | IndusFact`}
        description={listing.seo?.meta_description || (listing.description || '').slice(0, 160)}
        keywords={listing.seo?.keywords}
        canonical={typeof window !== 'undefined' ? `${window.location.origin}${listingPath(listing)}` : undefined}
        image={absoluteUrl(resolveEffectiveOgImage({ ...listing, og_image_url: listing.seo?.og_image_url || listing.og_image_url }, 'listing'))}
        schemaEndpoint={`/seo/listing/${listing.id}`}
        ogType='product'
      />
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem><BreadcrumbLink asChild><Link to='/'>Home</Link></BreadcrumbLink></BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to={typePath}>{typeLabel}</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          {listing.category_name && (
            <>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link
                    to={listingCategoryUrl(listing)}
                    data-testid='breadcrumb-category-link'
                  >
                    {listing.category_name}
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
            </>
          )}
          {listing.subcategory_name && (
            <>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link
                    to={listingCategoryUrl(listing, { includeSub: true })}
                    data-testid='breadcrumb-subcategory-link'
                  >
                    {listing.subcategory_name}
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
            </>
          )}
        </BreadcrumbList>
      </Breadcrumb>

      <div className='mt-4 grid lg:grid-cols-12 gap-6'>
        <div className='lg:col-span-8 space-y-6'>
          <div className='flex flex-col sm:flex-row items-start gap-3'>
            <div className='flex-1 min-w-0 w-full'>
              <div className='flex flex-wrap items-center gap-2 mb-1'>
                <span className={`text-[10px] px-2 py-1 rounded-full font-semibold uppercase ${typeBadge}`}>{listing.listing_type}</span>
                <StatusPill status={listing.status} />
                {listing.is_top && <span className='text-[10px] px-2 py-1 rounded-full font-semibold uppercase bg-primary text-primary-foreground'>Top</span>}
                {listing.is_featured && <span className='text-[10px] px-2 py-1 rounded-full font-semibold uppercase bg-accent text-accent-foreground'>Featured</span>}
                {listing.is_urgent && <span className='text-[10px] px-2 py-1 rounded-full font-semibold uppercase bg-[hsl(var(--danger))] text-white' data-testid='detail-badge-urgent'>Urgent</span>}
                {listing.is_gst_verified && (
                  <span
                    className='text-[10px] px-2 py-1 rounded-full font-semibold uppercase inline-flex items-center gap-1 bg-[hsl(var(--verified))]/10 text-[hsl(var(--verified))]'
                    data-testid='detail-badge-gst-verified'
                    title='Seller has been GST verified by IndusFact admin'
                  >
                    <BadgeCheck className='h-3 w-3' /> GST Verified
                  </span>
                )}
              </div>
              <h1 className='text-2xl sm:text-3xl font-semibold tracking-tight break-words'>{listing.title}</h1>
              {listing.status === 'sold' && (
                <div
                  className='mt-3 inline-flex items-center gap-2 rounded-md bg-emerald-600 text-white px-3 py-1.5 text-sm font-semibold'
                  data-testid='listing-detail-sold-badge'
                >
                  <BadgeCheck className='h-4 w-4' />
                  {listing.sold_closure_label === 'fulfilled' ? 'FULFILLED — no longer accepting inquiries' : 'SOLD — no longer available'}
                </div>
              )}
              {isIntlListing && (
                <div
                  className='mt-3 inline-flex items-center gap-1.5 rounded-md bg-amber-100 text-amber-900 border border-amber-200 px-3 py-1.5 text-sm font-semibold'
                  data-testid='listing-detail-intl-badge'
                >
                  <Globe className='h-4 w-4' /> International · Located in {listingCountry}
                </div>
              )}
              <div className='flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-sm text-muted-foreground'>
                <div className='flex items-center gap-1'><MapPin className='h-3.5 w-3.5 shrink-0' /><span>{[listing.locality, listing.city, listing.state, isIntlListing ? listingCountry : null].filter(Boolean).join(', ')}</span></div>
                <div className='flex items-center gap-1'><Tag className='h-3.5 w-3.5 shrink-0' /><span>{listing.subcategory_name || listing.category_name}</span></div>
                <div className='flex items-center gap-1'><Package className='h-3.5 w-3.5 shrink-0' /><span>{listing.view_count || 0} views</span></div>
              </div>
            </div>
            <div className='flex flex-wrap items-center gap-2 shrink-0'>
              <Button variant='outline' size='sm' onClick={() => setReportOpen(true)} data-testid='listing-detail-report-button'>
                <Flag className='h-4 w-4 mr-1' /> Report
              </Button>
              <ShareButton
                listingId={listing.id}
                title={listing.title}
                priceText={displayPrice != null ? `₹ ${Number(displayPrice).toLocaleString('en-IN')}` : 'Price on Request'}
              />
              <FavoriteButton listingId={listing.id} variant='default' data-testid='detail-favorite-btn' />
              {listing.status !== 'sold' && <CompareDetailButton listing={listing} />}
            </div>
          </div>

          {/* Gallery */}
          {images.length > 0 && (
            <div className='space-y-2'>
              <div className='relative aspect-[16/10] bg-muted rounded-xl overflow-hidden border'>
                <img
                  src={mediaUrl(currentImage.public_url)}
                  alt={listing.title}
                  className='w-full h-full object-contain bg-white'
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = PLACEHOLDER_IMG; }}
                />
              </div>
              {images.length > 1 && (
                <div className='flex gap-2 overflow-x-auto pb-2'>
                  {images.map((m, i) => (
                    <button
                      key={m.id || i}
                      onClick={() => setSelectedImage(i)}
                      className={`shrink-0 h-16 w-24 rounded-md overflow-hidden border-2 ${i === selectedImage ? 'border-primary' : 'border-transparent'}`}
                    >
                      <img
                        src={mediaUrl(m.thumb_url || m.public_url)}
                        alt=''
                        className='w-full h-full object-contain bg-white'
                        onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = PLACEHOLDER_IMG; }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Description */}
          <Card className='p-6'>
            <div className='font-semibold mb-2'>Description</div>
            <p className='text-sm whitespace-pre-line leading-relaxed'>{listing.description}</p>
          </Card>

          {/* Specs */}
          <Card className='p-6'>
            <div className='font-semibold mb-4'>Specifications</div>
            {specRows.length === 0 ? (
              <div className='text-sm text-muted-foreground'>Not provided</div>
            ) : (
              <dl className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                {specRows.map(([k, v]) => (
                  <div key={k} className='flex justify-between gap-4 border-b pb-2'>
                    <dt className='text-sm text-muted-foreground'>{k}</dt>
                    <dd className='text-sm font-medium text-right'>{String(v)}</dd>
                  </div>
                ))}
              </dl>
            )}
            {listing.attributes && Object.keys(listing.attributes || {}).length > 0 && (
              <div className='mt-6'>
                <div className='font-semibold text-sm mb-2'>Additional attributes</div>
                <dl className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                  {Object.entries(listing.attributes).map(([k, v]) => (
                    <div key={k} className='flex justify-between gap-4 border-b pb-2'>
                      <dt className='text-sm text-muted-foreground'>{k}</dt>
                      <dd className='text-sm font-medium text-right'>{String(v)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </Card>

          {(videos.length > 0 || pdfs.length > 0 || listing.video_url) && (
            <Card className='p-6 space-y-4'>
              <div className='font-semibold'>Attachments</div>
              {listing.video_url && (() => {
                const url = listing.video_url;
                let embedId = null;
                const m1 = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/i);
                if (m1) embedId = m1[1];
                if (!embedId) return null;
                return (
                  <div className='aspect-video rounded-lg overflow-hidden bg-black' data-testid='listing-detail-youtube'>
                    <iframe
                      title='Video'
                      src={`https://www.youtube.com/embed/${embedId}`}
                      className='w-full h-full'
                      frameBorder='0'
                      allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                      allowFullScreen
                    />
                  </div>
                );
              })()}
              {videos.map(v => (
                <div key={v.id}>
                  <video src={mediaUrl(v.public_url)} controls className='w-full rounded-lg bg-black' />
                </div>
              ))}
              {pdfs.map(p => (
                <div key={p.id}>
                  <a href={mediaUrl(p.public_url)} target='_blank' rel='noreferrer' className='text-primary hover:underline'>
                    Download brochure / spec sheet (PDF)
                  </a>
                </div>
              ))}
            </Card>
          )}

          {/* Reviews for the seller (visible on every listing) */}
          {listing.owner_id && (
            <Card className='p-6' data-testid='listing-detail-reviews-card'>
              <ReviewsWidget sellerId={listing.owner_id} isOwner={user && user.id === listing.owner_id} refreshKey={reviewRefreshKey} />
            </Card>
          )}
        </div>

        {/* Side panel */}
        <div className='lg:col-span-4 space-y-4'>
          <Card className='p-6 sticky top-20' data-testid='listing-detail-side-panel'>
            {displayPrice != null ? (
              <div className='mb-3'>
                <div className='text-2xl font-semibold tabular-nums'>₹ {Number(displayPrice).toLocaleString('en-IN')}</div>
                <div className='text-xs text-muted-foreground'>{priceType || ''}</div>
              </div>
            ) : (
              <div className='mb-3' data-testid='listing-detail-price-on-request'>
                <div className='text-xl font-semibold'>Price on Request</div>
                {priceType && priceType !== 'Price on Request' && (
                  <div className='text-xs text-muted-foreground'>{priceType}</div>
                )}
              </div>
            )}

            {/* Company block — visible to LOGGED-IN users without reveal (Batch A privacy update) */}
            {user && (listing.company_name_snapshot || listing.full_address) && (
              <div className='mb-4 rounded-lg border p-4 bg-secondary/40' data-testid='listing-detail-company-block'>                <div className='text-xs text-muted-foreground font-medium mb-2 flex items-center gap-1'>
                  <Building2 className='h-3 w-3' /> Seller company
                </div>
                {listing.company_name_snapshot && (
                  <div className='font-semibold text-sm flex items-center gap-2 flex-wrap'>
                    {listing.company_name_snapshot}
                    <RatingChip
                      avg={listing.seller_avg_rating}
                      count={listing.seller_review_count}
                      onClick={() => {
                        const el = document.querySelector('[data-testid="listing-detail-reviews-card"]');
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                    />
                    {listing.is_gst_verified && (
                      <span className='inline-flex items-center gap-1 text-[10px] font-medium rounded-full bg-[hsl(var(--verified))]/10 text-[hsl(var(--verified))] px-1.5 py-0.5'>
                        <BadgeCheck className='h-3 w-3' /> GST Verified
                      </span>
                    )}
                  </div>
                )}
                {listing.full_address && (
                  <div className='text-xs text-muted-foreground mt-1 flex items-start gap-1'>
                    <MapPin className='h-3 w-3 mt-0.5 shrink-0' />
                    <span>{listing.full_address}</span>
                  </div>
                )}
                {/* #11: View-Seller must render for logged-out viewers too;
                    optional chaining prevents the null-user crash that
                    silently hid the whole block on older listings viewed
                    without a login. Follow button routes anonymous users
                    to the sign-in page instead of erroring. */}
                {listing.owner_id && listing.owner_id !== user?.id && (
                  <div className='mt-3 flex flex-wrap gap-2'>
                    <Link to={`/seller/${listing.owner_id}`} className='inline-flex' data-testid='detail-view-seller-btn'>
                      <Button size='sm' variant='outline'>
                        <Building2 className='h-3.5 w-3.5 mr-1' /> View seller profile
                      </Button>
                    </Link>
                    <Button
                      size='sm'
                      variant={isFollowing ? 'outline' : 'default'}
                      onClick={() => user ? toggleFollow() : navigate('/login', { state: { from: `/listing/${id}` } })}
                      disabled={followBusy || (user && isFollowing === null)}
                      data-testid='detail-follow-btn'
                    >
                      <UserIcon className='h-3.5 w-3.5 mr-1' />
                      {user ? (isFollowing ? 'Following' : 'Follow') : 'Follow'}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* #11 (privacy revert): the fallback View-seller/Follow row
                is now gated to LOGGED-IN non-owners only. Logged-out users
                see NO seller/company affordance anywhere on the listing
                page (matches the Batch-A privacy stance from earlier). */}
            {user && listing.owner_id && listing.owner_id !== user.id &&
              !(listing.company_name_snapshot || listing.full_address) && (
                <div className='mb-4 flex flex-wrap gap-2' data-testid='listing-detail-seller-fallback-row'>
                  <Link to={`/seller/${listing.owner_id}`} className='inline-flex' data-testid='detail-view-seller-btn-fallback'>
                    <Button size='sm' variant='outline'>
                      <Building2 className='h-3.5 w-3.5 mr-1' /> View seller profile
                    </Button>
                  </Link>
                  <Button
                    size='sm'
                    variant={isFollowing ? 'outline' : 'default'}
                    onClick={toggleFollow}
                    disabled={followBusy || isFollowing === null}
                    data-testid='detail-follow-btn-fallback'
                  >
                    <UserIcon className='h-3.5 w-3.5 mr-1' />
                    {isFollowing ? 'Following' : 'Follow'}
                  </Button>
                </div>
              )}

            <div className='rounded-lg border p-4 bg-secondary/40' data-testid='listing-detail-masked-contact-block'>
              <div className='text-xs text-muted-foreground font-medium mb-2 flex items-center gap-1'>
                {revealed ? (
                  <><UserIcon className='h-3 w-3' /> Contact details</>
                ) : user ? (
                  <><Lock className='h-3 w-3' /> Contact hidden — click reveal to view</>
                ) : (
                  <><Lock className='h-3 w-3' /> Login to view seller contact</>
                )}
              </div>
              <div className='space-y-2 text-sm'>
                <div className='flex items-center gap-2'>
                  <UserIcon className='h-4 w-4 text-muted-foreground' />
                  <span className='text-muted-foreground'>Contact person:</span>
                  <span className='font-medium'>{revealed?.contact_person || (user ? '••• •••' : 'Login to view')}</span>
                </div>
                <div className='flex items-center gap-2'>
                  <Phone className='h-4 w-4 text-muted-foreground' />
                  <span className='text-muted-foreground'>Phone:</span>
                  {revealed?.mobile ? (
                    <>
                      <span className='font-medium mono'>{revealed.mobile}</span>
                      <button onClick={() => { navigator.clipboard.writeText(revealed.mobile); toast.success('Copied'); }} className='text-muted-foreground hover:text-foreground'><Copy className='h-3.5 w-3.5' /></button>
                    </>
                  ) : user ? (
                    <span className='mono'>{listing.mobile_masked || '+91-9XXXXXXX'}</span>
                  ) : (
                    <span className='text-muted-foreground'>Login to view</span>
                  )}
                </div>
                <div className='flex items-center gap-2'>
                  <Mail className='h-4 w-4 text-muted-foreground' />
                  <span className='text-muted-foreground'>Email:</span>
                  {revealed?.email ? (
                    <>
                      <span className='font-medium mono truncate'>{revealed.email}</span>
                      <button onClick={() => { navigator.clipboard.writeText(revealed.email); toast.success('Copied'); }} className='text-muted-foreground hover:text-foreground shrink-0'><Copy className='h-3.5 w-3.5' /></button>
                    </>
                  ) : user ? (
                    <span className='mono truncate'>{listing.email_masked || '•••@•••'}</span>
                  ) : (
                    <span className='text-muted-foreground'>Login to view</span>
                  )}
                </div>
                {listing.is_gst_verified && (
                  <div className='flex items-center gap-1 text-xs text-[hsl(var(--verified))]'>
                    <BadgeCheck className='h-3.5 w-3.5' /> Seller is GST verified
                  </div>
                )}
              </div>
            </div>

            <div className='mt-4 flex flex-col gap-2'>
              {listing.status === 'sold' ? (
                <div
                  className='rounded-md border border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/30 p-3 text-sm text-emerald-900 dark:text-emerald-200'
                  data-testid='listing-detail-sold-notice'
                >
                  This listing is closed. Contact reveal and new inquiries are disabled.
                  <Link
                    to={{
                      pathname: listingCategoryUrl(listing, { includeSub: true }) || listingCategoryUrl(listing) || typePath,
                      search: listing.state ? `?state=${encodeURIComponent(listing.state)}` : '',
                    }}
                    className='ml-1 underline font-medium'
                    data-testid='listing-detail-sold-browse-similar'
                  >
                    Browse similar listings →
                  </Link>
                </div>
              ) : (user && listing.owner_id === user.id) ? (
                // #17: The listing owner has nothing to reveal to themselves
                // and cannot send an inquiry to themselves. Replace the two
                // buyer-facing CTAs with a subtle owner affordance.
                <div
                  className='rounded-md border bg-secondary/40 p-3 text-sm text-muted-foreground'
                  data-testid='listing-detail-owner-hint'
                >
                  This is your listing. Buyers who visit this page will see the reveal &amp; inquiry actions.{' '}
                  <Link
                    to={`/post/${id}`}
                    className='underline font-medium text-foreground'
                    data-testid='listing-detail-edit-own-listing'
                  >
                    Edit listing →
                  </Link>
                </div>
              ) : (
                <>
                  {!revealed && (
                    <Button
                      onClick={handleReveal}
                      className='w-full bg-accent text-accent-foreground hover:bg-accent/90'
                      data-testid='listing-detail-reveal-contact-button'
                    >
                      {user ? <><Eye className='h-4 w-4 mr-1.5' /> Reveal Contact</> : <><Lock className='h-4 w-4 mr-1.5' /> Login to View Contact</>}
                    </Button>
                  )}
                  <Button
                    variant='default'
                    className='w-full'
                    onClick={() => user ? setInquiryOpen(true) : navigate('/login', { state: { from: `/listing/${id}` } })}
                    data-testid='listing-detail-send-inquiry-button'
                  >
                    <MessageSquare className='h-4 w-4 mr-1.5' /> Send Inquiry
                  </Button>
                  {/* WhatsApp CTA — paid sellers only. Rendered inside the
                      same button stack so it stays visually grouped with the
                      other contact options. */}
                  {listing?.owner_is_paid && (
                    <Button
                      variant='outline'
                      onClick={handleWhatsApp}
                      className='w-full border-emerald-500/50 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-950/40'
                      data-testid='listing-detail-whatsapp-button'
                    >
                      <WhatsAppIcon className='h-4 w-4 mr-1.5' /> Chat on WhatsApp
                    </Button>
                  )}
                </>
              )}
              {!user && (
                <div className='text-xs text-muted-foreground text-center'>
                  <Link to='/register' className='text-primary hover:underline'>Register free</Link> to contact and post listings
                </div>
              )}
            </div>
          </Card>
          {/* Sponsored — listing detail sidebar placement */}
          <SponsoredAdsRow placement='listing_side' listingType={listing?.listing_type} limit={1} variant='card' />
        </div>
      </div>

      {/* Similar listings — full-width row under the main listing panels.
          Rendered as a responsive grid rather than a horizontal scroller
          so it reads cleanly on mobile without scroll gestures. */}
      {similar.length > 0 && (
        <section
          className='mt-10 pt-6 border-t'
          aria-labelledby='similar-listings-heading'
          data-testid='similar-listings-section'
        >
          <div className='flex items-center justify-between mb-4'>
            <h2 id='similar-listings-heading' className='text-lg font-semibold tracking-tight'>
              Similar listings
            </h2>
            {(listing?.category_path_slug || listing?.category_slug) && (
              <Link
                to={listingCategoryUrl(listing)}
                className='text-xs font-medium text-primary hover:underline inline-flex items-center gap-0.5'
                data-testid='similar-view-all'
              >
                View all <ChevronRight className='h-3 w-3' />
              </Link>
            )}
          </div>
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'>
            {similar.map((l) => (
              <ListingCard key={l.id} listing={l} />
            ))}
          </div>
        </section>
      )}

      {/* Inquiry Dialog */}
      <Dialog open={inquiryOpen} onOpenChange={setInquiryOpen}>
        <DialogContent data-testid='inquiry-dialog'>
          <DialogHeader>
            <DialogTitle>Send inquiry</DialogTitle>
          </DialogHeader>
          <div className='space-y-3'>
            <div className='grid grid-cols-2 gap-3'>
              <div><Label>Your name</Label><Input data-testid='inquiry-name' value={inquiry.name} onChange={(e) => setInquiry({ ...inquiry, name: e.target.value })} /></div>
              <div><Label>Company</Label><Input data-testid='inquiry-company' value={inquiry.company} onChange={(e) => setInquiry({ ...inquiry, company: e.target.value })} /></div>
            </div>
            <div className='grid grid-cols-2 gap-3'>
              <div><Label>Email</Label><Input data-testid='inquiry-email' value={inquiry.email} onChange={(e) => setInquiry({ ...inquiry, email: e.target.value })} /></div>
              <div><Label>Mobile</Label><Input data-testid='inquiry-mobile' value={inquiry.mobile} onChange={(e) => setInquiry({ ...inquiry, mobile: e.target.value })} /></div>
            </div>
            <div>
              <Label>Your message</Label>
              <Textarea rows={5} data-testid='inquiry-message' value={inquiry.message} onChange={(e) => setInquiry({ ...inquiry, message: e.target.value })} placeholder={`Hi, I am interested in this ${listing.title}. Please share more details.`} />
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setInquiryOpen(false)}>Cancel</Button>
            <Button onClick={submitInquiry} disabled={inquirySending} data-testid='inquiry-submit-button'>
              <MessageSquare className='h-4 w-4 mr-1.5' /> {inquirySending ? 'Sending...' : 'Send inquiry'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Report this listing</DialogTitle></DialogHeader>
          <div className='space-y-3'>
            <div>
              <Label>Reason</Label>
              <Select value={report.reason} onValueChange={(v) => setReport({ ...report, reason: v })}>
                <SelectTrigger data-testid='report-reason-select'><SelectValue placeholder='Select reason' /></SelectTrigger>
                <SelectContent>
                  {REPORT_REASONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Details (optional)</Label>
              <Textarea rows={3} value={report.details} onChange={(e) => setReport({ ...report, details: e.target.value })} data-testid='report-details' />
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setReportOpen(false)}>Cancel</Button>
            <Button onClick={submitReport} data-testid='report-submit-button'><Flag className='h-4 w-4 mr-1.5' /> Submit report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ListingDetail;
